export type DspPortKind = 'audio' | 'control';

export interface DspPortDescriptor {
  readonly id: string;
  readonly kind: DspPortKind;
  readonly channels: number;
}

export interface DspParameterDescriptor {
  readonly id: string;
  readonly defaultValue: number;
  readonly minValue: number;
  readonly maxValue: number;
  readonly unit: string;
}

export interface DspModuleDescriptor {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly inputs: readonly DspPortDescriptor[];
  readonly outputs: readonly DspPortDescriptor[];
  readonly parameters: readonly DspParameterDescriptor[];
}

export interface DspInstanceContext {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
}

export interface DspProcessBlock {
  readonly frames: number;
  readonly inputs: Readonly<Record<string, Float32Array>>;
  readonly outputs: Readonly<Record<string, Float32Array>>;
  readonly controls: Readonly<Record<string, Float32Array>>;
}

export interface DspModuleInstance {
  reset(): void;
  setParameter(id: string, value: number): void;
  /** Process into caller-owned buffers. Implementations must not retain them. */
  process(block: DspProcessBlock): void;
}

export interface DspModuleDefinition {
  readonly descriptor: DspModuleDescriptor;
  createInstance(context: DspInstanceContext): DspModuleInstance;
}

const MODULE_ID = /^[a-z0-9]+(?:[.-][a-z0-9]+)+$/;
const LOCAL_ID = /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/;
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;

const assertFinite = (label: string, value: number): void => {
  if (!Number.isFinite(value)) throw new RangeError(`${label} must be finite`);
};

const freezePorts = (label: 'input' | 'output', ports: readonly DspPortDescriptor[]): readonly DspPortDescriptor[] => {
  const ids = new Set<string>();
  return Object.freeze(ports.map((port) => {
    if (!LOCAL_ID.test(port.id)) throw new RangeError(`${label} port id is invalid: ${port.id}`);
    if (ids.has(port.id)) throw new RangeError(`duplicate ${label} port: ${port.id}`);
    ids.add(port.id);
    if (port.kind !== 'audio' && port.kind !== 'control') throw new RangeError(`${label} port kind is invalid`);
    if (!Number.isInteger(port.channels) || port.channels < 1) {
      throw new RangeError(`${label} port channels must be a positive integer`);
    }
    return Object.freeze({ ...port });
  }));
};

const freezeParameters = (parameters: readonly DspParameterDescriptor[]): readonly DspParameterDescriptor[] => {
  const ids = new Set<string>();
  return Object.freeze(parameters.map((parameter) => {
    if (!LOCAL_ID.test(parameter.id)) throw new RangeError(`parameter id is invalid: ${parameter.id}`);
    if (ids.has(parameter.id)) throw new RangeError(`duplicate parameter: ${parameter.id}`);
    ids.add(parameter.id);
    assertFinite(`${parameter.id}.minValue`, parameter.minValue);
    assertFinite(`${parameter.id}.maxValue`, parameter.maxValue);
    assertFinite(`${parameter.id}.defaultValue`, parameter.defaultValue);
    if (parameter.minValue > parameter.maxValue) throw new RangeError(`${parameter.id}.minValue exceeds maxValue`);
    if (parameter.defaultValue < parameter.minValue || parameter.defaultValue > parameter.maxValue) {
      throw new RangeError(`${parameter.id}.defaultValue is outside its range`);
    }
    if (!parameter.unit) throw new RangeError(`${parameter.id}.unit is required`);
    return Object.freeze({ ...parameter });
  }));
};

/**
 * Defines a host-independent DSP package. The host owns graph scheduling,
 * buffers and UI; the package owns only its metadata and DSP instance.
 */
export const defineDspModule = (definition: DspModuleDefinition): DspModuleDefinition => {
  const descriptor = definition.descriptor;
  if (!MODULE_ID.test(descriptor.id)) throw new RangeError('descriptor id must be a stable dotted identifier');
  if (!descriptor.name.trim()) throw new RangeError('descriptor name is required');
  if (!SEMVER.test(descriptor.version)) throw new RangeError('descriptor version must be semantic');

  const frozenDescriptor: DspModuleDescriptor = Object.freeze({
    id: descriptor.id,
    name: descriptor.name,
    version: descriptor.version,
    inputs: freezePorts('input', descriptor.inputs),
    outputs: freezePorts('output', descriptor.outputs),
    parameters: freezeParameters(descriptor.parameters),
  });

  return Object.freeze({
    descriptor: frozenDescriptor,
    createInstance: definition.createInstance,
  });
};
